const STORE_SERVER_URL = process.env.REACT_APP_STORE_SERVER_URL || "http://localhost:8085";
const ORGANIZATION_ID = String(process.env.REACT_APP_IQ_ORGANIZATION_ID || "").trim();

function buildStoreUrl(path) {
    if (!ORGANIZATION_ID) {
        throw new Error("REACT_APP_IQ_ORGANIZATION_ID is not configured.");
    }

    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const storePath = normalizedPath.startsWith("/store") ? normalizedPath : `/store${normalizedPath}`;

    const url = new URL(`${STORE_SERVER_URL}${storePath}`);
    url.searchParams.set("organization_id", ORGANIZATION_ID);
    return url.toString();
}

async function StoreApiFetch(path, options = {}) {
    const {
        method = "GET",
        body = null,
        headers = {},
    } = options;

    const requestHeaders = {
        "Content-Type": "application/json",
        ...headers,
    };

    const response = await fetch(buildStoreUrl(path), {
        method,
        headers: requestHeaders,
        ...(body ? { body: JSON.stringify(body) } : {}),
    });

    let payload = {};
    try {
        payload = await response.json();
    } catch (error) {
        console.error("Error parsing JSON response:", error);
    }

    if (!response.ok) {
        throw new Error(payload.error || payload.message || "An error occurred while fetching data.");
    }

    return payload;
}

export async function getProducts() {
    return await StoreApiFetch("/products", {
        method: "GET",
    });
}

export async function getProductById(productId) {
    const response = await StoreApiFetch(`/products/${productId}`, {
        method: "GET",
    });

    return response.product;
}

export async function addToCart(payload) {
    const response = await StoreApiFetch("/cart", {
        method: "POST",
        body: payload,
    });
    return response;    
}

export async function getCart() {
    const cartToken = localStorage.getItem("cartToken");

    if (!cartToken) {
        return null; // or { cart: null }
    }

    const response = await StoreApiFetch(`/cart/${cartToken}`, {
        method: "GET",
    });

    return response.cart;
}

export async function updateCartItemQuantity(cartToken, variantId, newQuantity) {
    const response = await StoreApiFetch(`/cart/${cartToken}/updateQuantity/${variantId}`, {
        method: "POST",
        body: { quantity: newQuantity },
    });

    return response.cart;
}

export async function removeCartItem(cartToken, variantId) {
    const response = await StoreApiFetch(`/cart/${cartToken}/removeItem/${variantId}`, {
        method: "DELETE",
    });

    return response.cart;
}

export async function getAdditionalRate(cartToken, zipCode, quantity, address = null) {
    const normalizedZip = String(zipCode).replace(/\D/g, "").slice(0, 5);
    const normalizedQuantity = Number(quantity);

    const payload = {
        cart_token: cartToken,
        zip_code: normalizedZip,
        quantity: normalizedQuantity,
    };

    if (address) {
        payload.address = {
            address: address.address || "",
            city: address.city || "",
            state: address.state || "",
            postal_code: String(address.zip || "").replace(/\D/g, "").slice(0, 5),
            country: "US",
        };
    }

    const response = await StoreApiFetch("/checkout/AdditionalRate", {
        method: "POST",
        body: payload,
    });

    return response;
}

export async function createPaymentIntent(cartToken, deliveryInfo, billingInfo) {
    const response = await StoreApiFetch(`/checkout/payments/create-intent/${cartToken}`, {
        method: "POST",
        body: {
            destinationZIP: deliveryInfo.zip,
            deliveryInfo,
            billingInfo,
        },
    });

    const orderId = Number(response?.order_id ?? response?.order?.order_id);
    if (
        !response?.payment?.client_secret ||
        !response.payment.payment_intent_id ||
        !Number.isInteger(orderId) ||
        orderId <= 0
    ) {
        throw new Error("The checkout server returned an invalid payment response.");
    }

    return { ...response, orderId };
}

export async function createInventoryTransaction(cartToken, payload) {
    return await StoreApiFetch(`/checkout/inventory/transaction/${encodeURIComponent(cartToken)}`, {
        method: "POST",
        body: payload,
    });
}

export async function getOrderReceipt(orderId) {
    const response = await fetch(buildStoreUrl(`/checkout/receipt/${encodeURIComponent(orderId)}`));

    if (!response.ok) {
        let message = "Unable to retrieve the receipt.";
        try {
            const payload = await response.json();
            message = payload.error || payload.message || message;
        } catch (error) {
            console.error("Error parsing receipt error response:", error);
        }
        throw new Error(message);
    }

    return response.blob();
}


